import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ShieldAlert } from "lucide-react";
import { api, errMsg, fmtDateTime } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/Shared";

const STATUS = { pending: "bg-amber-50 text-amber-900", approved: "bg-green-50 text-green-900", rejected: "bg-red-50 text-red-900" };

/** DPDP Act: request erasure of one's personal data; the registry reviews it. */
export const DataRights = () => {
  const [req, setReq] = useState(null);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/me/deletion-request").then(({ data }) => setReq(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/me/deletion-request", { reason });
      toast.success("Deletion request submitted for review");
      setReason("");
      load();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="border border-border bg-card p-7" data-testid="data-rights-section">
      <p className="flex items-center gap-2 label-caps"><ShieldAlert className="h-3.5 w-3.5" /> Your data · DPDP Act, 2023</p>
      <p className="text-sm text-muted-foreground mt-3 max-w-2xl">
        Personal data on AdhocArb is processed only for the conduct of your references. One year after a reference is
        archived, inactive accounts are flagged for retention review. You may ask the registry to erase your personal
        data at any time; case dockets are kept with your identity anonymised.
      </p>
      {req && (
        <div className="mt-5 border border-border p-4 text-sm flex flex-wrap items-center justify-between gap-3" data-testid="deletion-request-status">
          <div>
            <p className="font-medium">Request of {fmtDateTime(req.created_at)}</p>
            <p className="text-xs text-muted-foreground mt-1">{req.reason}</p>
            {req.decision_reason && <p className="text-xs mt-1">Registry: {req.decision_reason}</p>}
          </div>
          <Badge className={STATUS[req.status]} testId="deletion-request-badge">{req.status}</Badge>
        </div>
      )}
      {(!req || req.status !== "pending") && (
        <form onSubmit={submit} className="mt-5 space-y-3" data-testid="deletion-request-form">
          <textarea required minLength={3} value={reason} onChange={(e) => setReason(e.target.value)} rows={3}
            placeholder="Why do you want your personal data erased?" data-testid="deletion-reason-input"
            className="w-full border border-border bg-background p-3 text-sm rounded-none" />
          <Button type="submit" disabled={busy} data-testid="deletion-request-button"
            className="rounded-none bg-red-800 hover:bg-red-700 text-white uppercase tracking-widest text-xs h-10 px-5">
            {busy ? "Submitting…" : "Request data deletion"}
          </Button>
        </form>
      )}
    </section>
  );
};
